/**
 * 🛰️ EVERYDAY OS - MASTER TYPE REGISTRY (v2.3 - Hybrid Scoping Build)
 */

import { Timestamp } from 'firebase/firestore';

export enum Role { OWNER = 'OWNER', ADMIN = 'ADMIN', MEMBER = 'MEMBER', GUEST = 'GUEST' }
export enum SafetyMode { STRICT = 'strict', RELAXED = 'relaxed', CHILD = 'child' }
export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'pending_approval' | 'done' | 'pantry' | 'history' | 'finalized';
export enum TemplateType { GROCERY = 'grocery', ACTION = 'action', MAINTENANCE = 'maintenance' }
export type PriorityTier = 'P1' | 'P2' | 'P3' | 'none';
export type TaskScope = 'personal' | 'household';
export type TaskSource = 'manual' | 'pet' | 'vehicle' | 'maintenance' | 'system';
export type RecurrenceRangeType = 'days' | 'weeks' | 'months' | 'years' | 'decades';
export type CalendarSource = 'personal' | 'household' | 'custom';
export type FocusLevel = 'deep' | 'shallow' | 'automated';
export type SubscriptionModule = 'DASHBOARD' | 'TASKS' | 'CALENDAR' | 'INVENTORY' | 'MAINTENANCE' | 'CONTACTS' | 'AUDIT' | 'SETTINGS';

// ─── UTS: New canonical types ─────────────────────────────────────────────────
export type RebootPolicy = 'reset' | 'archive' | 'persist';
export type TaskActionType =
  | 'CREATE' | 'MOVE' | 'DELETE' | 'UPDATE'
  | 'CONSUME' | 'MIGRATE' | 'RECUR' | 'SCOPE_MIGRATE' | 'SYNC';

/** Firestore-level permission keys checked in PermissionGate and taskActions */
export const TaskPermission = {
  CAN_VIEW_LOGS: 'can_view_logs',
  CAN_MOVE_TASKS: 'can_move_tasks',
  CAN_MANAGE_HOUSEHOLD: 'can_manage_household',
} as const;
export type TaskPermissionKey = typeof TaskPermission[keyof typeof TaskPermission];

/**
 * 🛡️ HYBRID SCOPING TYPES
 * Allows the UI to toggle visibility between partitions.
 */
export type DataScope = 'PERSONAL' | 'SHARED' | 'BOTH';

// --- 📞 CONTACTS ---
export type ContactCategory = 'professional' | 'personal' | 'emergency' | 'staff';
export type ContactStream = TaskSource | 'childcare' | 'medical' | 'legal' | 'general';
export interface ContactPhone { label: string; number: string; isPrimary: boolean; }
export interface ContactEmail { label: string; address: string; isPrimary: boolean; }
export interface ContactAddress { street: string; city: string; state: string; zip: string; }

export interface HouseholdContact {
  id: string;
  name: string;
  category: ContactCategory;
  stream: ContactStream;
  subCategory?: string;
  phones: ContactPhone[];
  emails: ContactEmail[];
  address?: ContactAddress;
  companyName?: string;
  notes?: string;
  tags: string[];
  isDeleted: boolean;
  localChangeTimestamp: number;
  scope: TaskScope; 
  ownerId?: string; 
  licenseNumber?: string;
  hourlyRate?: number;
}

// --- 📐 ENGINES ---
export interface BoardConfig {
  id: string;
  title: string;
  templateType: TemplateType;
  allowedStatuses: TaskStatus[];
  dataScope: DataScope; // <-- Add this
  icon: string;        // <-- Add this (e.g., 'list-todo' is a string)
  color: string;       // <-- Add this (e.g., '#4f46e5' is a string)
  visibility: string;
  isCollapsed: boolean;
  defaultGroupBy: string;
  activeFilters: string[];
}

export interface GroupedTasks { [key: string]: KanbanTask[]; }

export interface CustomCalendar {
  id: string;
  title: string;
  color: string;
  isVisible: boolean;
  ownerId: string;
  collectionName: string;
}

// --- 👤 USER & HOUSEHOLD ---
export interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: Role;
  safetyMode?: SafetyMode;
  acceptedLegalVersion: string;
  acceptedDisclaimerVersion: string;
  legalConsentDate?: string;
  preferredLanguage: string;
  timezone: string;
  autoDetectTz: boolean;
  activeSparkCategories: string[];
  createdAt: string;
  dashboardLayout?: WidgetLayout[];
  cookieConsent?: CookiePreferences;
  activeHouseholdId?: string | null;
  memberOf?: string[];
  defaultScope?: DataScope;
  permissions?: PermissionFlags;
  homeAddress?: string;
  referredBy?: string;
  hiddenFeatures?: string[];
  isInfluencer?: boolean;
  // Set server-side only, via the Admin SDK (googleOAuthCallback.ts / disconnectGoogleCalendar.ts)
  // — never trust a client-side write to this field as proof of an actual Google Calendar grant;
  // the real signal is whether googleAuthTokens/{uid} exists (Admin-SDK-only collection).
  googleOAuthAccepted?: boolean;
  platform?: 'web' | 'mobile';
  lastPlatformSeenAt?: string;
  subscription?: {
    tier: SubscriptionTier;
    startDate: string;
    endDate: string;
  };
  routines?: {
    bedtime?: {
      enabled: boolean;
      targetTime: string;
      steps: Array<{ offsetMins: number; label: string }>;
    };
  };
  elderModeEnabled?: boolean;
  elderFontSize?: 'medium' | 'large' | 'xl';
  accessibility?: AccessibilityPreferences;
  dietaryRestrictions?: string[];
}

export interface AccessibilityPreferences {
  colorBlindMode?: 'none' | 'deuteranopia' | 'protanopia' | 'tritanopia' | 'achromatopsia';
  fontScale?: 'normal' | 'large' | 'xl' | 'xxl';
  highContrast?: boolean;
  reducedMotion?: boolean;
  dyslexicFont?: boolean;
  enhancedFocus?: boolean;
  lineSpacing?: 'normal' | 'relaxed' | 'loose';
}

export interface CookiePreferences {
  essential: boolean;
  functionality: boolean; // Renamed from 'preferences' for clarity
  analytics: boolean;
  marketing: boolean;
  // Add any other specific cookie categories you track
  lastUpdated?: string;
}

/** 🛡️ Household Permission Matrix */
export interface PermissionFlags {
  canManageTasks: boolean;
  canDeleteTasks: boolean;
  canManageEvents: boolean;
  canManageGroceries: boolean;
  canDeleteGroceries: boolean; // Added canDeleteGroceries property
  canManageInventory: boolean; // Added canManageInventory property
  canManageContacts: boolean; // Added canManageContacts property
  canViewAuditLogs: boolean;   // Added canViewAuditLogs property
  role: Role;                  // Added role property
  joinedAt?: string;
}

export interface HouseholdSubscription {
  activeModules: SubscriptionModule[];
  tier?: SubscriptionTier;  // For tier-based feature gating
  enabledAddOns?: string[];   // feature IDs of purchased add-on modules e.g. ['pets', 'vehicles']
}

export interface Household {
  id: string;
  name: string;
  ownerId: string; // Added ownerId property
  memberIds: string[];
  members: HouseholdMember[]; // Added members property
  memberLimit?: number;
  permissions: { [userId: string]: PermissionFlags };
  activeInvite?: {
    code: string;
    expiresAt: string;
    createdBy: string;
    createdAt: string;
  } | null;
  type?: string;
  contacts?: any[]; // 🎯 Ensure this line is present
  address?: { // Also ensure address is defined if used elsewhere
    street: string;
    city: string;
    state: string;
    zip: string;
  } | null;
  subscription?: HouseholdSubscription; // Added subscription property
  property?: {
    purchasePrice?: number;
    purchaseDate?: string;
    currentEstimatedValue?: number;
    lastValueUpdateDate?: string;
    mortgageBalance?: number;
    mortgageLastUpdated?: string;
    squareFootage?: number;
    yearBuilt?: number;
    propertyType?: 'house' | 'condo' | 'townhouse' | 'apartment' | 'other';
    equityHistory?: Array<{ date: string; estimatedValue: number; mortgageBalance: number }>;
  };
  commuteBaseMinutes?: number;
  coParentingLinkId?: string;
  subscribedMunicipalityIds?: string[];
  linkedCareProfileIds?: string[];
}

export interface HouseholdMember {
  id: string; // Added id property
  userId: string;
  name: string;
  email: string;
  role: Role;
  joinedAt?: string;
  avatar?: string;
}

// --- 🛠️ TASKS & EVENTS ---
export interface KanbanTask {
  id: string;
  title: string;
  description?: string; // ADDED: description property
  status: TaskStatus;
  templateType: TemplateType;
  priorityTier?: PriorityTier; // ADDED: priorityTier property
  energyRequired?: number;
  tags: string[];
  source: TaskSource;
  ownerId?: string;
  createdBy?: string;
  boardId: string;
  lastBoardId: string | null;
  scope: TaskScope;
  isDeleted?: boolean;
  localChangeTimestamp?: number;
  dueDate?: string;
  completedAt?: string;
  assignedContactId?: string;
  lastModifiedBy: string;
  assignedTo: string;
  createdAt: string;
  visibilityRole: Role;
  isLocked: boolean;
  isBaseline: boolean;
  focusLevel: FocusLevel;
  contributionPoints: number;

  subtasks?: SubTask[];
  consumables?: AssetConsumable[];
  toolsRequired?: string[];
  documents?: TaskDocument[];

  estimatedTime: number;
  actualTime: number;
  consumption?: {
    quantity: number;
    remainingQuantity: number;
    unit: string;
    stockLevel?: number;
    averageCycle?: number;
    lastAddedHistory?: string;
    dayBetweenRecurrence?: number[];
    cost?: number;
  };
  recurrence?: {
    isEnabled: boolean;
    interval?: number;
    frequency?: RecurrenceRangeType | null;
    completionTrigger?: boolean;
    dayOfMonth?: number;
    lastResetDate?: string;
  };

  // ─── UTS Cat 2: Temporal ──────────────────────────────────────────────────
  rebootPolicy?: RebootPolicy;

  // ─── UTS Cat 3: Bio-Metric ───────────────────────────────────────────────
  moodImpact?: number; // +1 rewarding, -1 draining

  // ─── UTS Cat 4: Advanced Functional ─────────────────────────────────────
  monetaryValue?: number; // Cost of a repair part, purchase amount, etc.
  dependencies?: string[]; // Task IDs that must be 'done' before this becomes 'active'

  // ─── UTS Cat 5: Permission & Visibility ──────────────────────────────────
  auditTrail?: AuditTrailEntry[]; // Truncated local history of moves

  // ─── Sprint 7: Parental approval + rotating chores ───────────────────────
  assigneeId?: string;
  resolvedPendingApproval?: boolean;
  rotation?: {
    enabled: boolean;
    memberIds: string[];
    currentIndex: number;
  };
}

export interface SubTask {
  id: string;
  title: string;
  isCompleted: boolean;
  dueDate?: string;
  assignedTo?: string;
}

export interface AssetConsumable {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  lastPurchased?: string;
  costPerUnit?: number;
}

export interface TaskDocument {
  id: string;
  name: string;
  url: string;
  type: string; // e.g., 'pdf', 'image', 'text'
  uploadedAt: string;
}

/** Truncated per-task history: who moved this task and when */
export interface AuditTrailEntry {
  actorId: string;
  actionType: TaskActionType;
  timestamp: number;
  note?: string;
}

/** Entry in the localStorage PendingSync queue for offline-first writes */
export interface PendingSyncEntry {
  taskId: string;
  scope: TaskScope;
  updates: Partial<KanbanTask>;
  localChangeTimestamp: number;
  retryCount: number;
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;  
  color: string;
  source: 'personal' | 'household';
  scope: 'personal' | 'household';
  ownerId: string;
  location?: {
    address: string;
    
  };
  assignedContactId?: string;
  isSuggestion?: boolean;
  localChangeTimestamp?: number;
  isSlice?: boolean;
  isContinued?: boolean;
  isContinuation?: boolean;
  travelMetadata?: {
    estimatedDuration: number;
    originType: 'home' | 'previous_event';
    isImpossible: boolean;
    travelBackHomeAdvised: boolean;
  };
  customCalendarId?: string;
  type?: string; // Added type property for filtering in MultiColumnGrid
  category?: 'medical' | 'dental' | 'therapy' | 'specialist' | 'general' | string;
  arrivalBufferMins?: number;
  // Google-colorId-aligned swatch (see calendar-engine/utils/googleCalendarColors.ts) — kept
  // in addition to `color` (hex) so edits round-trip losslessly with Google Calendar sync.
  colorId?: string;
  // --- Recurrence (iCal/Google-style) ---
  // Lines are RRULE/EXDATE/RDATE strings (DTSTART is implied by this event's own `start`).
  // Only ever set on a "master" event; master events are never rendered directly — see
  // calendar-engine/utils/recurrence.ts, which expands them into virtual per-occurrence instances.
  recurrence?: string[];
  // Set on a materialized single-occurrence override (an event doc that has detached itself
  // from a master's recurrence to carry its own edits). Points back at the master event's id.
  recurringEventId?: string;
  // The occurrence slot (on the ORIGINAL, unedited schedule) this override replaces — used by
  // recurrence.ts to skip generating a virtual instance for that slot.
  originalStartTime?: { dateTime: string; timeZone: string };
  // Set on virtual (and materialized override) instances to point back at the master event's id.
  seriesId?: string;
  // True on a virtual (non-persisted) occurrence instance synthesized by recurrence.ts.
  isRecurrenceInstance?: boolean;
  // Stamped on every save — the only reliable per-user actor field for household-scope events
  // (ownerId holds the household id in that scope, not a user id).
  lastModifiedBy?: string;
}

export type EngineEvent = CalendarEvent;

export type WidgetId =
  | 'quick_access'
  | 'calendar_agenda'
  | 'tasks_summary'
  | 'maintenance_agenda'
  | 'activity_log'
  | 'sparks'
  | 'bills_summary'
  | 'pets_overview'
  | 'vehicles_summary'
  | 'leaderboard'
  | 'daily_brief'
  | 'health_score'
  | 'finance_summary'
  | 'meal_planner_week'
  | 'co_parenting_summary'
  | 'elder_care_summary'
  | 'appliance_overview'
  | 'location_overview';

export type ApplianceCategory = 'kitchen' | 'laundry' | 'hvac' | 'plumbing' | 'electrical' | 'other';
export type RecallStatus = 'clear' | 'recalled' | 'unknown';
export type ServiceRecordType = 'repair' | 'maintenance' | 'installation';
export type RiskScore = 'low' | 'medium' | 'high';

export interface Appliance {
  id: string;
  // Legacy fields (Maintenance.tsx compatibility)
  householdId: string;
  name: string;
  addedBy: string;
  serialNumber?: string;
  room?: string;
  warrantyExpiresDate?: string;
  notes?: string;
  createdAt: string;
  // Sprint 23 fields
  make: string;
  model: string;
  modelNumber?: string;
  purchaseDate?: string;
  warrantyExpiresAt?: string;
  manualUrl?: string;
  photoUrl?: string;
  category?: ApplianceCategory;
  recallStatus?: RecallStatus;
  totalServiceCost?: number;
  purchasePrice?: number;
}

export interface ServiceRecord {
  id: string;
  date: string;
  cost: number;
  contractor: string;
  notes: string;
  receiptPhotoUrl?: string;
  type: ServiceRecordType;
  createdAt: string;
}

export interface ApplianceRecall {
  id: string;
  recallId: string;
  title: string;
  description: string;
  hazard: string;
  remedy: string;
  url: string;
  detectedAt: string;
}

export interface RecipeIngredient {
  amount: string;  // e.g. "2", "1/2", "1.5"
  unit: string;
  item: string;
}

export interface RecipeStep {
  step: number;
  text: string;
}

export type GuestType = 'family' | 'friends' | 'colleagues' | 'other';

export interface ExternalGuest {
  name: string;
  type: GuestType;
}

export interface PlannedMealItem {
  recipeId?: string;
  recipeName: string;
}

export interface Recipe {
  id: string;
  householdId: string;
  name: string;
  servings: number;
  prepTimeMins?: number;
  cookTimeMins?: number;
  // Structured fields (new)
  ingredients: RecipeIngredient[];
  steps?: RecipeStep[];
  // Legacy free-text instructions (kept for backwards compat)
  instructions?: string;
  tags?: string[];
  sourceUrl?: string;
  imageUrl?: string;
  addedBy: string;
  createdAt: string;
  isFavorite?: boolean;
  isStarred?: boolean;
  leftoverDays?: number;
}

export interface HomeInventoryItem {
  id: string;
  householdId: string;
  room: string;
  name: string;
  estimatedValue: number;
  purchaseDate?: string;
  serialNumber?: string;
  notes?: string;
  addedBy: string;
  createdAt: string;
}

export interface HouseholdReward {
  id: string;
  householdId: string;
  name: string;
  pointCost: number;
  emoji?: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
}

export interface RewardRedemption {
  id: string;
  householdId: string;
  rewardId: string;
  rewardName: string;
  memberId: string;
  memberName: string;
  pointCost: number;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  resolvedAt?: string;
}

export interface MemberPoints {
  userId: string;
  householdId: string;
  totalPoints: number;
  monthlyPoints: number;
  lastUpdated: string;
}

export interface LayoutNotification {
  id: string;
  title: string;
  message: string;
  readBy?: string[];
  timestamp: any;
}

export interface WidgetLayout {
  // Define WidgetLayout based on how your dashboardLayout is structured
  // This is a placeholder; adjust according to your actual layout type
  i: string; // ID
  x: number; // X position
  y: number; // Y position
  w: number; // Width
  h: number; // Height
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
}

export interface DailySpark {
  // Define DailySpark structure
  id: string;
  content: string;
  date: string;
}

export interface Intelligence {
  sparks: any[]; // Adjust 'any' to a more specific type if sparks have a defined structure
}

export interface FeaturePoll {
  id: string;
  title: string; // Changed from 'question' to 'title'
  options: PollOption[];
  totalVotes: number;
  isActive: boolean;
  createdAt: Timestamp;
  createdBy?: string; // Made optional as per example data
}

export interface PollOption {
  id: string;
  text: string;
  votes: number;
  votedUserIds?: string[]; // New: Track which users voted for this option
}

export enum SubscriptionTier {
  FREE = 'FREE',
  BASIC = 'BASIC',
  PREMIUM = 'PREMIUM',
}

export interface AuditLogEntry {
  // ─── Core identity ────────────────────────────────────────────────────────
  id: string;
  timestamp: number;
  scope: TaskScope;

  // ─── Actor ────────────────────────────────────────────────────────────────
  actorId: string;         // UID of who made the change
  userId?: string;         // Backward-compat alias for actorId
  userName?: string;       // Display name (backward-compat)

  // ─── Action ───────────────────────────────────────────────────────────────
  actionType: TaskActionType;
  action?: string;         // Backward-compat alias

  // ─── Target ───────────────────────────────────────────────────────────────
  targetId?: string;       // Task or entity ID
  entityType?: 'TASK' | 'EVENT' | 'MEMBER' | 'SYSTEM'; // backward-compat
  entityName?: string;     // backward-compat
  taskTitle?: string;      // Human-readable label for activity feed

  // ─── State transition ─────────────────────────────────────────────────────
  previousState?: string;
  newState?: string;
  fromStatus?: TaskStatus;
  toStatus?: TaskStatus;
  fromBoardId?: string;
  toBoardId?: string;

  // ─── Sync metadata ────────────────────────────────────────────────────────
  syncType?: 'realtime' | 'reconciliation';
  originatingDevice?: string;
  isOfflineSync?: boolean;

  // ─── Extras ───────────────────────────────────────────────────────────────
  metadata?: Record<string, any>;
}

// --- ⚛️ STATE ---
export interface SubscriptionStatus {
  tier: 'free' | 'basic' | 'premium';
  status: 'active' | 'past_due' | 'canceled' | 'trialing' | 'incomplete' | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: number | null;
  stripeSubscriptionId: string | null;
}

export interface AppState {
  currentUser: UserProfile | null;
  currentHouseholdId: string | null;
  activeDataScope: DataScope;
  households: Household[];
  tasks: KanbanTask[];
  userProfiles: UserProfile[];
  inventory: KanbanTask[];
  personalEvents: CalendarEvent[]; // 🎯 RENAMED from calendarEvents
  householdEvents: CalendarEvent[]; // 🎯 ADDED
  customEvents: EngineEvent[];
  maintenanceTasks: KanbanTask[];
  notifications: LayoutNotification[];
  theme: string;
  isBooting: boolean;
  contacts: HouseholdContact[];
  dailySpark: DailySpark | null;
  lastSyncTimestamp: number | null;
  intelligence: Intelligence;
  auditLogs: AuditLogEntry[];
  dashboardLayout: WidgetLayout[] | null;
  customGroceryCategories: {
    personal: Record<string, string[]>;
    household: Record<string, string[]>;
  };
  subscriptionStatus: SubscriptionStatus | null;
  // Firebase Auth's emailVerified — NOT Firestore-sourced (unlike currentUser), since it can
  // change client-side via auth.currentUser.reload() without any Firestore write. Defaults to
  // true so the brief window before the real onAuthStateChanged value lands never flashes a
  // false "please verify" block.
  emailVerified: boolean;
}


export type AppAction =
  | { type: 'SET_USER'; payload: UserProfile | null }
  | { type: 'UPDATE_USER'; payload: Partial<UserProfile> }
  | { type: 'SET_BOOTING'; payload: boolean }
  | { type: 'SET_EMAIL_VERIFIED'; payload: boolean }
  | { type: 'SET_HOUSEHOLDS'; payload: Household[] }
  | { type: 'SET_CURRENT_HOUSEHOLD'; payload: string | null }
  | { type: 'ADD_HOUSEHOLD'; payload: Household }
  | { type: 'SET_DATA_SCOPE'; payload: DataScope }
  | { type: 'SET_TASKS'; payload: KanbanTask[] }
  | { type: 'ADD_TASK'; payload: KanbanTask }
  | { type: 'UPDATE_TASK'; payload: { id: string; updates: Partial<KanbanTask> } }
  | { type: 'SET_INVENTORY'; payload: KanbanTask[] }
  | { type: 'SET_PERSONAL_EVENTS'; payload: CalendarEvent[] } // 🎯 MODIFIED
  | { type: 'ADD_PERSONAL_EVENT'; payload: CalendarEvent } // 🎯 ADDED
  | { type: 'UPDATE_PERSONAL_EVENT'; payload: { id: string; updates: Partial<CalendarEvent> } } // 🎯 ADDED
  | { type: 'SET_HOUSEHOLD_EVENTS'; payload: CalendarEvent[] } // 🎯 ADDED
  | { type: 'ADD_HOUSEHOLD_EVENT'; payload: CalendarEvent } // 🎯 ADDED
  | { type: 'UPDATE_HOUSEHOLD_EVENT'; payload: { id: string; updates: Partial<CalendarEvent> } } // 🎯 ADDED
  | { type: 'SET_MAINTENANCE_TASKS'; payload: KanbanTask[] }
  | { type: 'SET_NOTIFICATIONS'; payload: LayoutNotification[] }
  | { type: 'SET_THEME'; payload: string }
  | { type: 'TOGGLE_THEME' }
  | { type: 'SET_SYNC'; payload: number | null }
  | { type: 'UPDATE_LAYOUT'; payload: any }
  | { type: 'SET_CONTACTS'; payload: HouseholdContact[] }
  | { type: 'REMOVE_HOUSEHOLD'; payload: string }
  | { type: 'UPDATE_HOUSEHOLD'; payload: { id: string; updates: Partial<Household> } }
  | { type: 'ADD_CONTACT'; payload: HouseholdContact }
  | { type: 'REMOVE_CONTACT'; payload: string }
  | { type: 'REMOVE_TASK'; payload: string }
  | { type: 'DELETE_PERSONAL_EVENT'; payload: string }
  | { type: 'DELETE_HOUSEHOLD_EVENT'; payload: string }
  | {
      type: 'SAVE_CUSTOM_GROCERY_CATEGORIES';
      payload: {
        scope: 'personal' | 'household';
        categories: Record<string, string[]>;
        userId: string;
        householdId: string | null;
      };
    }
  | {
      type: 'SET_CUSTOM_GROCERY_CATEGORIES';
      payload: {
        scope: 'personal' | 'household';
        categories: Record<string, string[]>;
      };
    }
  | { type: 'UPDATE_ACCESSIBILITY'; payload: Partial<AccessibilityPreferences> }
  | { type: 'SET_SUBSCRIPTION_STATUS'; payload: SubscriptionStatus | null };

// ─── Sprint 11: Financial Intelligence ───────────────────────────────────────
export interface IncomeSource {
  id: string; label: string; amount: number;
  frequency: 'monthly' | 'bi-weekly' | 'weekly' | 'annual'; memberId?: string;
}
export interface SavingsGoal {
  id: string; label: string; targetAmount: number; currentAmount: number;
  targetDate?: string; monthlyContribution: number;
}
export interface HouseholdLedger {
  incomeSources: IncomeSource[]; savingsGoals: SavingsGoal[];
  spendBaseline: Record<string, number>; baselineLastSet?: string;
  currency: string; lastUpdated: string;
}
export interface PropertyProfile {
  address: string; estimatedValue: number; mortgageBalance: number;
  purchasePrice: number; purchaseDate: string; lastUpdated: string;
}

// ─── Sprint 10: Wellness, Family & Smart Routines ─────────────────────────────
export interface Medication {
  id: string; name: string; dosage: string;
  frequency: 'daily' | 'twice_daily' | 'weekly' | 'as_needed';
  times: string[]; startDate: string; endDate?: string | null;
  currentQuantity: number; refillThreshold: number;
  prescribedBy?: string; linkedContactId?: string; notes?: string;
  scope: 'personal' | 'household'; assignedToMemberId?: string; nextRefillDate?: string;
}
export interface ScheduledReminder {
  id: string; userId: string;
  type: 'medication_dose' | 'refill_alert' | 'routine_step';
  scheduledFor: Timestamp; payload: Record<string, any>;
  status: 'pending' | 'sent' | 'cancelled'; createdAt: Timestamp;
}
export interface FamilyGoal {
  id: string; householdId: string; title: string;
  targetCount: number; completedCount: number;
  targetFrequency: 'weekly' | 'bi_weekly' | 'monthly';
  membersRequired: string[]; sessionDurationMins: number;
  createdBy: string; createdAt: string;
}
export type RoleView = 'admin' | 'member' | 'child';

// ─── Sprint 13: Co-Parenting ──────────────────────────────────────────────────
export type CustodyPattern = 'week-on-week-off' | '2-2-3' | '5-2-2-5' | 'custom';
export type SwapStatus = 'pending' | 'accepted' | 'declined' | 'countered' | 'withdrawn';
export type CoParentRole = 'primary' | 'secondary';
export type SharedExpenseCategory = 'medical' | 'education' | 'clothing' | 'activity' | 'other';

export interface CustodyConfig {
  weekOnWeekOff?: { startDate: string; firstHouseholdId: string };
  twoTwoThree?: { anchorDate: string };
  fiveTwoTwoFive?: { anchorDate: string; firstHouseholdId: string };
  custom?: Array<{ householdId: string; startDate: string; endDate: string }>;
  holidayOverrides?: Array<{ id: string; name: string; date: string; householdId: string }>;
}

export interface CoParentingLink {
  id: string;
  householdA: { householdId: string; label: string; role: CoParentRole };
  householdB: { householdId: string; label: string; role: CoParentRole } | null;
  custodyPattern: CustodyPattern;
  custodyConfig: CustodyConfig;
  timezone: string;
  status: 'pending' | 'active';
  inviteCode?: string;
  inviteExpiresAt?: string;
  createdAt: string;
  createdBy: string;
}

export interface ChildMedication {
  id: string; name: string; dose: string; schedule: string; prescribedBy?: string;
}

export interface ChildActivity {
  id: string; name: string; dayOfWeek: string; time: string; location?: string;
  drivingHousehold: string | 'both';
}

export interface SharedChild {
  id: string; firstName: string; lastName: string; dateOfBirth: string;
  schoolName?: string; schoolGrade?: string; photoUrl?: string;
  allergies: string[];
  medications: ChildMedication[];
  activities: ChildActivity[];
  doctorName?: string; doctorPhone?: string;
  emergencyContacts: Array<{ id: string; name: string; relation: string; phone: string }>;
  notes?: string; lastUpdatedBy: string; lastUpdated: string;
  pendingDeletion?: { requestedBy: string; requestedAt: string; confirmedBy?: string };
}

export interface CustodySwapRequest {
  id: string; requestedBy: string; requestedAt: string;
  originalDate: string; proposedDate?: string; oneWay: boolean;
  reason?: string; status: SwapStatus;
  respondedBy?: string; respondedAt?: string;
  counterProposedDate?: string; counterReason?: string;
}

export interface SharedExpense {
  id: string; childId: string | 'all'; description: string;
  amount: number; currency: string; category: SharedExpenseCategory;
  paidBy: string; date: string; receiptUrl?: string;
  addedBy: string; addedAt: string;
}

export interface HandoffNote {
  id: string; childId?: string; fromHouseholdId: string;
  message: string; isUrgent: boolean; handoffDate: string;
  createdAt: string; readBy: string[];
}

// ─── Sprint 14: Municipal Services ───────────────────────────────────────────
export type MunicipalCategory = 'garbage'|'recycling'|'organics'|'bulky'|'other';
export type MunicipalEventCategory = 'concert'|'market'|'public_meeting'|'sport'|'festival'|'other';
export type AnnouncementCategory = 'notice'|'alert'|'update'|'general';
export type AnnouncementStatus = 'published'|'draft';
export type EventStatus = 'published'|'draft'|'cancelled';
export type MunicipalStatus = 'active'|'inactive';

export interface MunicipalRecurrence {
  weekday: number;
  interval: 'weekly'|'biweekly';
  biweeklyGroup?: 'A'|'B';
  startDate: string;
}

export interface MunicipalSchedule {
  id: string;
  title: string;
  category: MunicipalCategory;
  color: string;
  recurrence: MunicipalRecurrence;
  notes?: string;
  isActive: boolean;
  createdAt: string;
}

export interface MunicipalException {
  id: string;
  scheduleId: string;
  scheduleTitle: string;
  originalDate: string;
  newDate: string|null;
  reason: string;
  createdAt: string;
}

export interface MunicipalEvent {
  id: string;
  title: string;
  description: string;
  startDateTime: string;
  endDateTime: string;
  location: string;
  category: MunicipalEventCategory;
  imageUrl?: string;
  registrationUrl?: string;
  status: EventStatus;
  createdAt: string;
  updatedAt: string;
}

export interface MunicipalAnnouncement {
  id: string;
  title: string;
  body: string;
  isUrgent: boolean;
  category: AnnouncementCategory;
  status: AnnouncementStatus;
  expiresAt?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Municipality {
  id: string;
  name: string;
  region: string;
  timezone: string;
  logoUrl?: string;
  contactEmail: string;
  status: MunicipalStatus;
  assignedAdminUids: string[];
  subscriberCount: number;
  createdAt: string;
  createdBy: string;
}
// ─── Sprint 15: Elder Care Types ─────────────────────────────────────────────

// ── Union Types ──────────────────────────────────────────────────────────────
export type DnrStatus = 'none' | 'full' | 'dnr-cc' | 'partial';
export type CareRole = 'care-admin' | 'contributor' | 'viewer';
export type LivingSituation = 'co-resident' | 'independent' | 'assisted-living';
export type TransportType = 'household-member' | 'medical-transport' | 'rideshare' | 'other';
export type AppointmentType = 'gp' | 'specialist' | 'dental' | 'therapy' | 'lab' | 'imaging' | 'other';
export type IncidentType = 'fall' | 'confusion' | 'breathing' | 'pain' | 'behavioural' | 'injury' | 'other';
export type MedFrequency = 'daily' | 'twice_daily' | 'three_times_daily' | 'weekly' | 'as_needed';
export type AdherenceStatus = 'taken' | 'skipped' | 'deferred';
export type TransportRequestStatus = 'requested' | 'confirmed' | 'completed' | 'cancelled';
export type AppointmentStatus = 'upcoming' | 'completed' | 'cancelled';
export type IncidentSeverity = 'minor' | 'moderate' | 'serious';
export type NoteType = 'observation' | 'concern' | 'question' | 'positive' | 'general';
export type DocumentCategory = 'dnr' | 'poa' | 'healthcare-proxy' | 'living-will' | 'insurance' | 'id' | 'legal' | 'other';
export type CareTeamRole = 'gp' | 'specialist' | 'dentist' | 'pharmacist' | 'physiotherapist' | 'home-aide' | 'social-worker' | 'other';

// ── Interfaces ───────────────────────────────────────────────────────────────
export interface DnrChangeEntry {
  changedBy: string;
  changedAt: string;
  previousStatus: DnrStatus;
  newStatus: DnrStatus;
}

export interface CareCircleMember {
  userId: string;
  name: string;
  email: string;
  role: CareRole;
  joinedAt: string;
}

export interface InsuranceInfo {
  provider: string;
  memberId: string;
  groupId?: string;
  phone?: string;
}

export interface CareProfile {
  id: string;
  memberUids: string[];
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  photoUrl?: string;
  dnrStatus: DnrStatus;
  dnrDocumentUrl?: string;
  dnrSigningDate?: string;
  dnrPhysicianName?: string;
  dnrPhysicalCopyLocation?: string;
  dnrLastReviewedDate?: string;
  dnrNextReviewDate?: string;
  dnrChangeLog: DnrChangeEntry[];
  primaryConditions: string[];
  allergies: string[];
  bloodType?: string;
  livingSituation: LivingSituation;
  facilityName?: string;
  facilityAddress?: string;
  facilityRoom?: string;
  facilityContactName?: string;
  facilityContactPhone?: string;
  mobilityAids: string[];
  mobilityNotes?: string;
  drivingStatus?: 'active' | 'restricted' | 'retired';
  dietaryRestrictions: string[];
  insurancePrimary?: InsuranceInfo;
  insuranceSecondary?: InsuranceInfo;
  careCircle: CareCircleMember[];
  linkedHouseholdIds: string[];
  elderLinkedUid?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CareVital {
  id: string;
  date: string;
  bloodPressureSystolic?: number;
  bloodPressureDiastolic?: number;
  heartRate?: number;
  weight?: number;
  bloodGlucose?: number;
  spo2?: number;
  temperature?: number;
  notes?: string;
  loggedBy: string;
  loggedAt: string;
}

export interface CareCheckIn {
  id: string;
  date: string;
  painLevel: 0 | 1 | 2;
  generalFeeling: 'same' | 'worse' | 'better';
  needsHelp: boolean;
  notes?: string;
  loggedBy: string;
  loggedAt: string;
}

export interface CareMedication {
  id: string;
  name: string;
  dosage: string;
  frequency: MedFrequency;
  scheduledTimes: string[];
  startDate: string;
  endDate?: string;
  currentQuantity: number;
  refillThreshold: number;
  nextRefillDate?: string;
  prescribedBy?: string;
  pharmacyName?: string;
  pharmacyPhone?: string;
  linkedCareTeamContactId?: string;
  notes?: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
}

export interface MedAdherence {
  id: string;
  medicationId: string;
  medicationName: string;
  scheduledDate: string;
  scheduledTime: string;
  status: AdherenceStatus;
  takenAt?: string;
  loggedBy: string;
  notes?: string;
}

export interface CareAppointment {
  id: string;
  title: string;
  appointmentType: AppointmentType;
  providerName: string;
  providerPhone?: string;
  linkedCareTeamContactId?: string;
  dateTime: string;
  location?: string;
  companionMemberId?: string;
  companionHouseholdId?: string;
  prepNotes?: string;
  postNotes?: string;
  followUpActions?: string[];
  status: AppointmentStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CareIncident {
  id: string;
  date: string;
  type: IncidentType;
  description: string;
  location?: string;
  injuryAssessment?: string;
  medicalAttentionSought: boolean;
  medicalAttentionDetails?: string;
  severity: IncidentSeverity;
  loggedBy: string;
  loggedAt: string;
}

export interface CareTransportRequest {
  id: string;
  appointmentId?: string;
  date: string;
  time: string;
  destination: string;
  purpose?: string;
  transportType: TransportType;
  assignedMemberId?: string;
  assignedHouseholdId?: string;
  assignedMemberName?: string;
  externalProviderName?: string;
  externalProviderPhone?: string;
  status: TransportRequestStatus;
  notes?: string;
  createdBy: string;
  createdAt: string;
}

export interface CareNote {
  id: string;
  type: NoteType;
  message: string;
  isUrgent: boolean;
  authorId: string;
  authorName: string;
  createdAt: string;
}

export interface CareDocument {
  id: string;
  label: string;
  category: DocumentCategory;
  fileUrl: string;
  uploadedBy: string;
  uploadedAt: string;
  notes?: string;
}

export interface CareTeamContact {
  id: string;
  name: string;
  role: CareTeamRole;
  specialtyOrAgency?: string;
  phone: string;
  email?: string;
  address?: string;
  notes?: string;
  addedBy: string;
  createdAt: string;
}

// --- 📍 LOCATION & GEOFENCING (Sprint 26) ---
export interface LocationPrefs {
  sharing: boolean;
  pausedUntil: Timestamp | null;
  lastUpdated: Timestamp;
}

export interface MemberLocation {
  lat: number;
  lng: number;
  accuracy: number;
  batteryLevel: number | null;
  timestamp: Timestamp;
  lastKnown: boolean;
}

export interface Geofence {
  id: string;
  name: string;
  lat: number;
  lng: number;
  radiusMeters: number;
  notifyOnArrive: boolean;
  notifyOnDepart: boolean;
  createdBy: string;
  createdAt: Timestamp;
}

export interface GeofenceEvent {
  id: string;
  memberId: string;
  memberName: string;
  zoneName: string;
  type: 'arrive' | 'depart';
  timestamp: Timestamp;
}

export interface Checkin {
  id: string;
  userId: string;
  userName: string;
  zoneName: string;
  timestamp: Timestamp;
}
